import { Search } from '@mui/icons-material'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography
} from '@mui/material'
import { memo, useCallback, useContext, useEffect, useState } from 'react'
import { GetCategoriasTienda } from '../../../services/productos.service'
import { IntegracionesContext } from '../../../context/integraciones.context'
import { CategoryWoo } from '../../../types/responses'
import { useAppStore } from '../../../../../store/app-store'
import { TenantsContext } from '../../../../../context/tenants.context'
import ButtonRounded from '../../../../../components/Common/utils/button/ButtonRounded'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { CustomDatePicker, CustomInput } from '../../../../../components/Common/input/CustomInput'
import dayjs from 'dayjs'
import { Moment } from 'moment'

const SearchNovedades = () => {
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const { tiendaSeleccionada, getNovedades } = useContext(IntegracionesContext)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [categoriesWoo, setCategoriesWoo] = useState<CategoryWoo[]>([])
  const codLocal = useAppStore((state) => state.codLocal)
  const { tenant } = useContext(TenantsContext)
  const [dateSince, setDateSince] = useState<dayjs.Dayjs | null>(null)
  const [dateUntil, setDateUntil] = useState<dayjs.Dayjs | null>(null)

  const filterNovedades = useCallback(
    async (search: string, category: string) => {
      setSearching(true)
      try {
        if (!tiendaSeleccionada) return
        await getNovedades(codLocal, category === 'todos' ? '' : category, search)
      } catch (e) {
        console.log(e)
      } finally {
        setSearching(false)
      }
    },
    [tiendaSeleccionada, codLocal, getNovedades]
  )

  const handleSearch = async () => {
    await getNovedades(codLocal, category === 'todos' ? '' : category, search)
  }

  const getCategorias = useCallback(async () => {
    if (!tiendaSeleccionada) return null
    const data = await GetCategoriasTienda(tenant.path, tiendaSeleccionada.name, codLocal)

    setCategoriesWoo(data.data.data)
  }, [tiendaSeleccionada, tenant.path, codLocal])

  useEffect(() => {
    getCategorias()
  }, [getCategorias])

  const handleChangeSearch = (e: any) => setSearch(e.target.value)

  const handleChangeRubro = (e: any) => setCategory(e.target.value)

  const handleChangeDate = useCallback((from: 'until' | 'since', date: Moment) => {
    if (from === 'since') {
      return setDateSince(dayjs(date.toDate()))
    } else {
      return setDateUntil(dayjs(date.toDate()))
    }
  }, [])

  const clearFilters = () => {
    setSearch('')
    setCategory('')
    setDateSince(null)
    setDateUntil(null)
    setOpen(false)
    filterNovedades('', '')
  }

  return (
    <>
      <ButtonRounded startIcon={<Search />} onClick={() => setOpen(!open)}>
        Buscar Productos
      </ButtonRounded>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', p: '8px', bgcolor: '#F5F5F7' }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography fontSize={{ xs: 18, sm: 20, md: 24 }} fontWeight="bold">
            Buscar productos desde {tiendaSeleccionada.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap="20px">
            <FormControl fullWidth>
              <CustomInput
                fullWidth
                placeholder="Buscar"
                value={search}
                onChange={handleChangeSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <CustomInput
                select
                native
                fullWidth
                defaultValue="todos"
                onChange={handleChangeRubro}
                sx={{ bgcolor: 'white' }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              >
                <MenuItem value="todos">Todas las categorías</MenuItem>
                {categoriesWoo.length > 0 &&
                  categoriesWoo.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </CustomInput>
            </FormControl>

            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap="16px" width="100%">
              <FormControl fullWidth>
                <CustomDatePicker
                  fullWidth
                  label="Desde"
                  value={dateSince}
                  slots={{ openPickerIcon: CalendarMonthOutlinedIcon }}
                  onChange={(date) => setDateSince(date)}
                  format="DD/MM/YYYY"
                  maxDate={dateUntil}
                  disableFuture
                />
              </FormControl>
              <FormControl fullWidth>
                <CustomDatePicker
                  fullWidth
                  label="Hasta"
                  value={dateUntil}
                  slots={{ openPickerIcon: CalendarMonthOutlinedIcon }}
                  onChange={(date) => setDateUntil(date)}
                  format="DD/MM/YYYY"
                  minDate={dateSince}
                  disableFuture
                />
              </FormControl>
            </Box>

            <ButtonRounded
              bgcolor="#5983EF"
              textColor="white"
              variant="contained"
              fullWidth
              onClick={() => filterNovedades(search, category)}
              disabled={searching}
            >
              {searching ? 'Buscando' : 'Buscar'}
            </ButtonRounded>
            <ButtonRounded variant="outlined" disabled={searching} onClick={clearFilters} fullWidth>
              Limpiar filtros
            </ButtonRounded>
            <ButtonRounded borderSize="0" variant="text" onClick={() => setOpen(false)} fullWidth>
              Volver
            </ButtonRounded>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default memo(SearchNovedades)
